import { AxiosResponse } from 'axios';
import { axiosClient } from 'config/Axios/AxiosInstanceConfigs';
import { LandingPageApiResponse, InfoCardData } from 'Interfaces/ProductWikiInterfaces';
import { WikiSearchResult } from 'screens/BDPortal/ProductWiki/components/LandingPage/components/SearchSection/searchSection.types';
import {
  OpportunityAtAGlanceApiResponse,
  OpportunityAtAGlanceRequestParams,
  OpportunityTilesApiResponse,
  OpportunitySparklinesApiResponse,
  OpportunityMedicalSupplyApiResponse,
  OpportunityMedicalSupplyRequestParams,
} from 'Interfaces/ProductWikiInterfaces/opportunityAtAGlance.types';
import {
  MarketLandscapeApiResponse,
  MarketLandscapeRequestParams,
} from 'Interfaces/ProductWikiInterfaces/marketLandscape.types';
import {
  PricingApiResponse,
  PricingRequestParams,
  PriceElasticityData,
  PricingCurrentBandApiResponse,
  PricingHistoricalTrendApiResponse,
  PricingLabelerApiResponse,
} from 'Interfaces/ProductWikiInterfaces/pricing.types';
import {
  CompetitorsApiResponse,
  CompetitorsRequestParams,
  CompetitorsOverviewApiResponse,
  CompetitorsAndaApiResponse,
  CompetitorsMarketChartsApiResponse,
  CompetitorsOverviewRequestParams,
  CompetitorsAndaRequestParams,
  CompetitorsMarketChartsRequestParams,
} from 'Interfaces/ProductWikiInterfaces/competitors.types';
import {
  RegulatoryApiResponse,
  RegulatoryRequestParams,
} from 'Interfaces/ProductWikiInterfaces/regulatory.types';
import { DataSourcesApiResponse } from 'Interfaces/ProductWikiInterfaces/dataSources.types';
import {
  APIFDFApiResponse,
  APIFDFImportData,
  APIFDFExportData,
  PaginatedAPIFDFResponse,
  APIFDFPaginationParams,
} from 'Interfaces/ProductWikiInterfaces/apifdf.types';
import {
  AIInsightsRequestParams,
  AIInsightsApiResponse,
} from 'Interfaces/ProductWikiInterfaces/aiInsights.types';
import {
  COTDistributionApiResponse,
  COTDistributionRequestParams,
} from 'Interfaces/ProductWikiInterfaces/cotDistribution.types';
import {
  LabelerInformationApiResponse,
  LabelerInformationRequestParams,
} from 'Interfaces/ProductWikiInterfaces/labelerInformation.types';
import {
  NADWaiversApiResponse,
  NADWaiversRequestParams,
} from 'Interfaces/ProductWikiInterfaces/nadWaivers.types';
import {
  DrugEstablishmentsApiResponse,
  DrugEstablishmentsRequestParams,
} from 'Interfaces/ProductWikiInterfaces/drugEstablishments.types';
import {
  TaaDesignatedCountriesApiResponse,
  TaaDesignatedCountriesRequestParams,
} from 'Interfaces/ProductWikiInterfaces/taaDesignatedCountries.types';
import { MoleculeMarketInsightsApiResponse } from 'Interfaces/ProductWikiInterfaces/moleculeMarketInsights.types';

/**
 * Product Wiki API Services
 * Handles all API calls for the Product Wiki module
 */

const PRODUCT_WIKI_BASE_URL = '/wiki';

/**
 * Fetch landing page data including stats, ANDA updates, contracts, etc.
 * @returns Promise with landing page data
 */
export const getLandingPageData = async (): Promise<AxiosResponse<LandingPageApiResponse>> => {
  try {
    const response = await axiosClient.get<LandingPageApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/landing-page/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch contract expirations by days
 * @param days - Number of days to look forward (e.g., 90, 180, 365)
 * @returns Promise with contract expirations data
 */
export const getContractExpirations = async (
  days: number
): Promise<AxiosResponse<InfoCardData[]>> => {
  try {
    const response = await axiosClient.get<InfoCardData[]>(
      `${PRODUCT_WIKI_BASE_URL}/get-contract-expirations/`,
      {
        params: { days },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch ANDA updates by days
 * @param days - Number of days to look forward (e.g., 90, 180, 365)
 * @returns Promise with ANDA updates data
 */
export const getAndaUpdates = async (days: number): Promise<AxiosResponse<InfoCardData[]>> => {
  try {
    const response = await axiosClient.get<InfoCardData[]>(
      `${PRODUCT_WIKI_BASE_URL}/get-anda-updates/`,
      {
        params: { days },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch Loss of Exclusivity by days
 * @param days - Number of days to look forward (e.g., 90, 180, 365)
 * @returns Promise with Loss of Exclusivity data
 */
export const getLossOfExclusivity = async (
  days: number
): Promise<AxiosResponse<InfoCardData[]>> => {
  try {
    const response = await axiosClient.get<InfoCardData[]>(
      `${PRODUCT_WIKI_BASE_URL}/get-loss-of-exclusivity/`,
      {
        params: { days },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch Pre-Solicitation Alerts by type
 * @param type - Type of filter - 'duedate' or 'deadline'
 * @returns Promise with Pre-Solicitation Alerts data
 */
export const getPreSolicitationAlerts = async (
  type: string
): Promise<AxiosResponse<InfoCardData[]>> => {
  try {
    const response = await axiosClient.get<InfoCardData[]>(
      `${PRODUCT_WIKI_BASE_URL}/get-pre-solicitation-alerts/`,
      {
        params: { type },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch posted solicitations that are not archived
 * @returns Promise with posted solicitations data
 */
export const getPostedSolicitations = async (): Promise<AxiosResponse<InfoCardData[]>> => {
  try {
    const response = await axiosClient.get<InfoCardData[]>(
      `${PRODUCT_WIKI_BASE_URL}/get-posted-solicitations/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Solicitation details response interface
 */
export interface SolicitationHistoryItem {
  id: number;
  status: string;
  posted_date: string | null;
  created_at: string | null;
  last_modified_at: string | null;
  last_modified_by: string | null;
  flag: boolean;
  due_date?: string | null;
  deadline_date?: string | null;
}

export interface SolicitationDetails {
  id: number;
  solicitation_number: string;
  title: string;
  status: string;
  posted_date: string | null;
  due_date: string | null;
  deadline_date: string | null;
  archive_date: string | null;
  uiLink: string | null;
  resourceLink: string | null;
  parent_short_name: string | null;
  full_parent_name: string | null;
  noticeId: string | null;
  isResponseDeadlineUpdated: boolean;
  responseDeadlineUpdatedDate: string | null;
  previousResponseDeadline: string | null;
  created_at: string | null;
  last_modified_at: string | null;
  last_modified_by: string | null;
  flag: boolean;
}

export interface SolicitationDetailsResponse {
  success: boolean;
  solicitation_number: string;
  current: SolicitationDetails | null;
  history: SolicitationHistoryItem[];
  total_records: number;
}

/**
 * Fetch solicitation details by solicitation number
 * @param solicitationNumber - The solicitation number to fetch details for
 * @returns Promise with solicitation details and history
 */
export const getSolicitationDetails = async (
  solicitationNumber: string
): Promise<AxiosResponse<SolicitationDetailsResponse>> => {
  try {
    const response = await axiosClient.get<SolicitationDetailsResponse>(
      `${PRODUCT_WIKI_BASE_URL}/get-solicitation-details/${encodeURIComponent(solicitationNumber)}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Search for molecules, concepts, and NDCs
 * @param query - Search term
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with search results
 */
export const searchWikiItems = async (
  query: string,
  signal?: AbortSignal
): Promise<AxiosResponse<WikiSearchResult[]>> => {
  try {
    const response = await axiosClient.post<WikiSearchResult[]>(
      `${PRODUCT_WIKI_BASE_URL}/fetch-options/`,
      { query },
      { signal }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch comprehensive molecule/concept/NDC search data
 * @param item - Search term (molecule name, concept name, or NDC code)
 * @param type - Type of search: 'molecule', 'concept', or 'ndc'
 * @param label - Display text for search history (optional)
 * @returns Promise with complete molecule search data
 */
export const getMoleculeSearchData = async (
  item: string,
  type: 'molecule' | 'concept' | 'ndc',
  label?: string
): Promise<AxiosResponse<any>> => {
  try {
    const response = await axiosClient.post<any>(`${PRODUCT_WIKI_BASE_URL}/molecule-search/`, {
      item,
      type,
      label: label || item,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch molecule info and stats data
 * Returns molecule basic info (name, formula, therapeutic class) and key statistics
 * @param item - Search term (molecule name, concept name, or NDC code)
 * @param type - Type of search: 'molecule', 'concept', or 'ndc'
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with molecule info and stats data
 */
export const getMoleculeInfoStats = async (
  item: string,
  type: 'molecule' | 'concept' | 'ndc',
  signal?: AbortSignal
): Promise<AxiosResponse<any>> => {
  try {
    const response = await axiosClient.post<any>(
      `${PRODUCT_WIKI_BASE_URL}/molecule-info-stats/`,
      { item, type },
      { signal }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch molecule strength variants data
 * Returns detailed information about all strength variants for a molecule
 * @param item - Search term (molecule name, concept name, or NDC code)
 * @param type - Type of search: 'molecule', 'concept', or 'ndc'
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with strength variants data
 */
export const getMoleculeStrengths = async (
  item: string,
  type: 'molecule' | 'concept' | 'ndc',
  signal?: AbortSignal
): Promise<AxiosResponse<any>> => {
  try {
    const response = await axiosClient.post<any>(
      `${PRODUCT_WIKI_BASE_URL}/molecule-strengths/`,
      { item, type },
      { signal }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch molecule ANDA (Abbreviated New Drug Application) data
 * Returns detailed ANDA information including application numbers, applicant holders, etc.
 * @param item - Search term (molecule name, concept name, or NDC code)
 * @param type - Type of search: 'molecule', 'concept', or 'ndc'
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with ANDA data
 */
export const getMoleculeAndaData = async (
  item: string,
  type: 'molecule' | 'concept' | 'ndc',
  signal?: AbortSignal
): Promise<AxiosResponse<any>> => {
  try {
    const response = await axiosClient.post<any>(
      `${PRODUCT_WIKI_BASE_URL}/molecule-anda/`,
      { item, type },
      { signal }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Opportunity At a Glance data
 * @param params - Request parameters (gfc, period, optional dates for custom)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with Opportunity At a Glance data
 */
export const getOpportunityAtAGlance = async (
  params: OpportunityAtAGlanceRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<OpportunityAtAGlanceApiResponse>> => {
  try {
    const { gfc, period, from_date, to_date } = params;
    const queryParams: any = {
      gfc,
      period,
    };

    if (period === 'custom' && from_date && to_date) {
      queryParams.from_date = from_date;
      queryParams.to_date = to_date;
    }

    const response = await axiosClient.get<OpportunityAtAGlanceApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/opportunity-at-a-glance/`,
      {
        params: queryParams,
        signal,
      }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Opportunity Tiles data (split from opportunity-at-a-glance)
 * Returns tiles (Gov TAM, Gov Volume, Price Band, HHI, Contract Types) and LOE status
 * @param params - Request parameters (gfc, period, optional dates for custom)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with tiles and LOE status data
 */
export const getOpportunityTiles = async (
  params: OpportunityAtAGlanceRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<OpportunityTilesApiResponse>> => {
  try {
    const { gfc, period, from_date, to_date } = params;
    const queryParams: any = {
      gfc,
      period,
    };

    if (period === 'custom' && from_date && to_date) {
      queryParams.from_date = from_date;
      queryParams.to_date = to_date;
    }

    const response = await axiosClient.get<OpportunityTilesApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/opportunity-tiles/`,
      {
        params: queryParams,
        signal,
      }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Opportunity Sparklines data (split from opportunity-at-a-glance)
 * Returns sales and volume trend arrays for sparkline charts
 * @param params - Request parameters (gfc, period, optional dates for custom)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with sparkline trend data
 */
export const getOpportunitySparklines = async (
  params: OpportunityAtAGlanceRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<OpportunitySparklinesApiResponse>> => {
  try {
    const { gfc, period, from_date, to_date } = params;
    const queryParams: any = {
      gfc,
      period,
    };

    if (period === 'custom' && from_date && to_date) {
      queryParams.from_date = from_date;
      queryParams.to_date = to_date;
    }

    const response = await axiosClient.get<OpportunitySparklinesApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/opportunity-sparklines/`,
      {
        params: queryParams,
        signal,
      }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Medical Supply Companies data for bubble chart
 * @param params - Request parameters (gfc, period, optional dates for custom)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with medical supply companies data for bubble chart
 */
export const getMedicalSupplyCompanies = async (
  params: OpportunityMedicalSupplyRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<OpportunityMedicalSupplyApiResponse>> => {
  try {
    const { gfc, period, from_date, to_date } = params;
    const queryParams: any = {
      gfc,
      period,
    };

    if (period === 'custom' && from_date && to_date) {
      queryParams.from_date = from_date;
      queryParams.to_date = to_date;
    }

    const response = await axiosClient.get<OpportunityMedicalSupplyApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/opportunity-medical-supply/`,
      {
        params: queryParams,
        signal,
      }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Market Landscape data
 * @param params - Request parameters (gfc)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with Market Landscape data
 */
export const getMarketLandscape = async (
  params: MarketLandscapeRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<MarketLandscapeApiResponse>> => {
  try {
    const { gfc, period, from_date, to_date } = params;
    const queryParams: any = { gfc };

    if (period) {
      queryParams.period = period;
    }

    if (period === 'custom' && from_date && to_date) {
      queryParams.from_date = from_date;
      queryParams.to_date = to_date;
    }

    const response = await axiosClient.get<MarketLandscapeApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/market-landscape/`,
      {
        params: queryParams,
        signal,
      }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Pricing Intelligence data
 * Returns both monthly (12 months) and quarterly (4 quarters) trend data in a single response.
 * @param params - Request parameters (gfc)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with Pricing Intelligence data including trendMonthly and trendQuarterly
 */
export const getPricingData = async (
  params: PricingRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<PricingApiResponse>> => {
  try {
    const { gfc } = params;
    const response = await axiosClient.get<PricingApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/pricing/`,
      {
        params: { gfc },
        signal,
      }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Price Elasticity data for the Price Elasticity chart
 * Returns price-volume relationship data with trend line for visualization.
 * @param params - Request parameters (gfc)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with Price Elasticity data including trend line and elasticity coefficient
 */
export const getPriceElasticityData = async (
  params: PricingRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<PriceElasticityData>> => {
  try {
    const { gfc } = params;
    const response = await axiosClient.get<PriceElasticityData>(
      `${PRODUCT_WIKI_BASE_URL}/price-elasticity/`,
      {
        params: { gfc },
        signal,
      }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Current Market Price Band (Last 90 Days) data
 * Returns box plot statistics: min, q1, median, q3, max, spread.
 * @param params - Request parameters (gfc)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with current price band data
 */
export const getPricingCurrentBand = async (
  params: PricingRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<PricingCurrentBandApiResponse>> => {
  try {
    const { gfc } = params;
    const response = await axiosClient.get<PricingCurrentBandApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/pricing-current-band/`,
      {
        params: { gfc },
        signal,
      }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Historical Pricing Trend data
 * Returns both monthly (12 months) and quarterly (4 quarters) trend data.
 * @param params - Request parameters (gfc)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with historical trend data
 */
export const getPricingHistoricalTrend = async (
  params: PricingRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<PricingHistoricalTrendApiResponse>> => {
  try {
    const { gfc } = params;
    const response = await axiosClient.get<PricingHistoricalTrendApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/pricing-historical-trend/`,
      {
        params: { gfc },
        signal,
      }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Current Price per Labeler data
 * Returns the latest price for each labeler from VA/DOD sources.
 * @param params - Request parameters (gfc)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with current price per labeler data
 */
export const getPricingLabeler = async (
  params: PricingRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<PricingLabelerApiResponse>> => {
  try {
    const { gfc } = params;
    const response = await axiosClient.get<PricingLabelerApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/pricing-labeler/`,
      {
        params: { gfc },
        signal,
      }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Competitors Tab data (legacy - all data in one call)
 * @param params - Request parameters (gfc, page, page_size)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with Competitors Tab data
 */
export const getCompetitorsData = async (
  params: CompetitorsRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<CompetitorsApiResponse>> => {
  try {
    const { gfc, page, page_size } = params;
    const queryParams: any = { gfc };

    if (page !== undefined) {
      queryParams.page = page;
    }
    if (page_size !== undefined) {
      queryParams.page_size = page_size;
    }

    const response = await axiosClient.get<CompetitorsApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/competitors/`,
      {
        params: queryParams,
        signal,
      }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Competitors Overview data (barriers, timeline, manufacturers)
 * Split endpoint for independent loading
 * @param params - Request parameters (gfc)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with competitors overview data
 */
export const getCompetitorsOverview = async (
  params: CompetitorsOverviewRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<CompetitorsOverviewApiResponse>> => {
  try {
    const { gfc } = params;
    const response = await axiosClient.get<CompetitorsOverviewApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/competitors/overview/`,
      {
        params: { gfc },
        signal,
      }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Competitors ANDA Applications data (paginated)
 * Split endpoint for independent loading and pagination
 * @param params - Request parameters (gfc, page, page_size)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with ANDA applications data
 */
export const getCompetitorsAnda = async (
  params: CompetitorsAndaRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<CompetitorsAndaApiResponse>> => {
  try {
    const {
      gfc,
      page,
      page_size,
      search,
      application_number,
      market_status,
      active_ingredient,
      dosage_form,
      route,
      strength,
      rld,
      rs,
      applicant_holder,
      last_submission_type,
      last_submission_date_from,
      last_submission_date_to,
    } = params;
    const queryParams: Record<string, string | number> = { gfc };

    if (page !== undefined) queryParams.page = page;
    if (page_size !== undefined) queryParams.page_size = page_size;
    if (search !== undefined && search !== '') queryParams.search = search;
    if (application_number !== undefined && application_number !== '')
      queryParams.application_number = application_number;
    if (market_status !== undefined && market_status !== '')
      queryParams.market_status = market_status;
    if (active_ingredient !== undefined && active_ingredient !== '')
      queryParams.active_ingredient = active_ingredient;
    if (dosage_form !== undefined && dosage_form !== '')
      queryParams.dosage_form = dosage_form;
    if (route !== undefined && route !== '') queryParams.route = route;
    if (strength !== undefined && strength !== '') queryParams.strength = strength;
    if (rld !== undefined && rld !== '') queryParams.rld = rld;
    if (rs !== undefined && rs !== '') queryParams.rs = rs;
    if (applicant_holder !== undefined && applicant_holder !== '')
      queryParams.applicant_holder = applicant_holder;
    if (last_submission_type !== undefined && last_submission_type !== '')
      queryParams.last_submission_type = last_submission_type;
    if (last_submission_date_from !== undefined && last_submission_date_from !== '')
      queryParams.last_submission_date_from = last_submission_date_from;
    if (last_submission_date_to !== undefined && last_submission_date_to !== '')
      queryParams.last_submission_date_to = last_submission_date_to;

    const response = await axiosClient.get<CompetitorsAndaApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/competitors/anda/`,
      {
        params: queryParams,
        signal,
      }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Competitors Market Charts data (sales trend, market share)
 * Split endpoint for independent loading
 * @param params - Request parameters (gfc)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with market charts data
 */
export const getCompetitorsMarketCharts = async (
  params: CompetitorsMarketChartsRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<CompetitorsMarketChartsApiResponse>> => {
  try {
    const { gfc } = params;
    const response = await axiosClient.get<CompetitorsMarketChartsApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/competitors/market-charts/`,
      {
        params: { gfc },
        signal,
      }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch NAD Waivers data for Manufacturer tab
 * @param params - Request parameters (gfc)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with active_count and waivers list
 */
export const getNadWaivers = async (
  params: NADWaiversRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<NADWaiversApiResponse>> => {
  try {
    const { gfc, page, page_size, search } = params;
    const requestParams: Record<string, string | number> = { gfc };
    if (page !== undefined) requestParams.page = page;
    if (page_size !== undefined) requestParams.page_size = page_size;
    if (search !== undefined && search !== '') requestParams.search = search;
    const response = await axiosClient.get<NADWaiversApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/nad-waivers/`,
      {
        params: requestParams,
        signal,
      }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Labeler Information data for Manufacturer tab
 * @param params - Request parameters (gfc)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with labeler_information list
 */
export const getLabelerInformation = async (
  params: LabelerInformationRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<LabelerInformationApiResponse>> => {
  try {
    const { gfc, page, page_size, search } = params;
    const requestParams: Record<string, string | number> = { gfc };
    if (page !== undefined) requestParams.page = page;
    if (page_size !== undefined) requestParams.page_size = page_size;
    if (search !== undefined && search !== '') requestParams.search = search;
    const response = await axiosClient.get<LabelerInformationApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/labeler-information/`,
      {
        params: requestParams,
        signal,
      }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch TAA Designated Countries list for the modal (global list)
 * @param params - Optional pagination and search (page, page_size, search)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with countries list and total_count, page, page_size
 */
export const getTaaDesignatedCountries = async (
  params?: TaaDesignatedCountriesRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<TaaDesignatedCountriesApiResponse>> => {
  try {
    const requestParams: Record<string, string | number> = {};
    if (params?.page !== undefined) requestParams.page = params.page;
    if (params?.page_size !== undefined) requestParams.page_size = params.page_size;
    if (params?.search !== undefined && params.search !== '')
      requestParams.search = params.search;
    const response = await axiosClient.get<TaaDesignatedCountriesApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/taa-designated-countries/`,
      { params: requestParams, signal }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Drug Establishment Registration data for the Search Establishments modal
 * @param params - Request parameters (gfc, optional page, page_size, search)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with establishments list and total_count, page, page_size
 */
export const getDrugEstablishments = async (
  params: DrugEstablishmentsRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<DrugEstablishmentsApiResponse>> => {
  try {
    const { gfc, page, page_size, search } = params;
    const requestParams: Record<string, string | number> = { gfc };
    if (page !== undefined) requestParams.page = page;
    if (page_size !== undefined) requestParams.page_size = page_size;
    if (search !== undefined && search !== '') requestParams.search = search;
    const response = await axiosClient.get<DrugEstablishmentsApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/drug-establishments/`,
      {
        params: requestParams,
        signal,
      }
    );
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Regulatory Tab data
 * @param params - Request parameters (gfc)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with Regulatory Tab data
 */
export const getRegulatoryData = async (
  params: RegulatoryRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<RegulatoryApiResponse>> => {
  try {
    const { gfc } = params;
    const response = await axiosClient.get<RegulatoryApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/regulatory/`,
      {
        params: { gfc },
        signal,
      }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch Data Sources
 * @returns Promise with data sources data
 */
export const getDataSources = async (): Promise<AxiosResponse<DataSourcesApiResponse>> => {
  try {
    const response = await axiosClient.get<DataSourcesApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/data-sources/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch APIFDF Import and Export data (Legacy - returns all data)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with APIFDF import and export data
 */
export const getAPIFDFData = async (
  signal?: AbortSignal
): Promise<AxiosResponse<APIFDFApiResponse>> => {
  try {
    const response = await axiosClient.get<APIFDFApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/get-apifdf-data/`,
      { signal }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch paginated APIFDF data for either import or export
 * @param params - Pagination parameters including type, page, pageSize, and optional filters
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with paginated APIFDF data and total record count
 */
export const getPaginatedAPIFDFData = async <T extends APIFDFImportData | APIFDFExportData>(
  params: APIFDFPaginationParams,
  signal?: AbortSignal
): Promise<AxiosResponse<PaginatedAPIFDFResponse<T>>> => {
  try {
    // Build query params
    const queryParams = new URLSearchParams({
      type: params.type,
      page: params.page.toString(),
      page_size: params.pageSize.toString(),
    });

    // Add molecule and strength filters if provided (filters description containing these values)
    if (params.molecule) {
      queryParams.append('molecule', params.molecule);
    }
    if (params.strength) {
      queryParams.append('strength', params.strength);
    }

    // Add filters as JSON string if provided
    if (params.filters && Object.keys(params.filters).length > 0) {
      queryParams.append('filters', JSON.stringify(params.filters));
    }

    const response = await axiosClient.get<PaginatedAPIFDFResponse<T>>(
      `${PRODUCT_WIKI_BASE_URL}/get-apifdf-data/?${queryParams.toString()}`,
      { signal }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch COT Distribution data for pie chart visualization
 * @param params - Request parameters (gfc, period, optional dates for custom)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with COT Distribution data
 */
export const getCOTDistribution = async (
  params: COTDistributionRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<COTDistributionApiResponse>> => {
  try {
    const { gfc, period, from_date, to_date } = params;
    const queryParams: any = { gfc };

    if (period) {
      queryParams.period = period;
    }

    if (period === 'custom' && from_date && to_date) {
      queryParams.from_date = from_date;
      queryParams.to_date = to_date;
    }

    const response = await axiosClient.get<COTDistributionApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/cot-distribution/`,
      {
        params: queryParams,
        signal,
      }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Generate AI Insights for various analysis types
 * @param params - Request parameters (insight_type, gfc, period_option)
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with AI generated insights data
 */
export const generateAIInsights = async (
  params: AIInsightsRequestParams,
  signal?: AbortSignal
): Promise<AxiosResponse<AIInsightsApiResponse>> => {
  try {
    const response = await axiosClient.post<AIInsightsApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/ai/generate-insights/`,
      params,
      { signal }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

/**
 * Fetch molecule-level market insights data
 * Calculates comprehensive market intelligence metrics aggregated at the molecule level
 * for the "Market Insights & Trends" section
 * @param moleculeName - Name of the molecule to analyze
 * @param signal - AbortSignal for canceling requests
 * @returns Promise with molecule market insights data
 */
export const getMoleculeMarketInsights = async (
  moleculeName: string,
  signal?: AbortSignal
): Promise<AxiosResponse<MoleculeMarketInsightsApiResponse>> => {
  try {
    const response = await axiosClient.post<MoleculeMarketInsightsApiResponse>(
      `${PRODUCT_WIKI_BASE_URL}/ai/molecule-market-insights/`,
      { molecule_name: moleculeName },
      { signal }
    );
    return response;
  } catch (error) {
    // Don't log error if request was aborted
    if (error instanceof Error && error.name === 'CanceledError') {
      throw error;
    }
    throw error;
  }
};

export const productWikiServices = {
  getLandingPageData,
  getContractExpirations,
  getAndaUpdates,
  getLossOfExclusivity,
  getPreSolicitationAlerts,
  getPostedSolicitations,
  getSolicitationDetails,
  searchWikiItems,
  getMoleculeSearchData,
  // Molecule search split APIs
  getMoleculeInfoStats,
  getMoleculeStrengths,
  getMoleculeAndaData,
  getOpportunityAtAGlance,
  // Opportunity split APIs
  getOpportunityTiles,
  getOpportunitySparklines,
  getMedicalSupplyCompanies,
  getMarketLandscape,
  getCOTDistribution,
  getPricingData,
  getPriceElasticityData,
  // Pricing split APIs
  getPricingCurrentBand,
  getPricingHistoricalTrend,
  getPricingLabeler,
  getCompetitorsData,
  // Competitors split APIs
  getCompetitorsOverview,
  getCompetitorsAnda,
  getCompetitorsMarketCharts,
  getNadWaivers,
  getLabelerInformation,
  getTaaDesignatedCountries,
  getDrugEstablishments,
  getRegulatoryData,
  getDataSources,
  getAPIFDFData,
  getPaginatedAPIFDFData,
  generateAIInsights,
  getMoleculeMarketInsights,
};
